import { renderCell, renderHeader } from '../components/table';
import { useMemo, useContext, useState, useEffect } from 'react';
import { Flex, Box, Icon } from '@chakra-ui/react';
import { BreakdownBy } from '../components/breakdown-by';
import { useDispatch, useSelector } from 'react-redux';
import { applyGroup } from '@/store/atsReports/pipeline';
import { ArrowRightIcon } from '@heroicons/react/outline';
import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';
import { CenterSpinner, Table } from '@/components/ats';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { KText } from '@/ui-library/components';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';

interface Option {
  label: string;
  id: number | string;
}

const groupByBase = [
  { id: null, label: 'Time', key: 'dt' },
  { id: 'job_name', label: 'Job name' },
  { id: 'primary_recruiter', label: 'Primary recruiter' },
  { id: 'primary_hm', label: 'Primary hiring manager' },
  { id: 'appl_source', label: 'Source (on application)' },
  { id: 'department', label: 'Department' },
  // { id: 'appl_location', label: 'Location (on application)' },
  { id: 'rejection_type', label: 'Rejection type' },
  { id: 'rejection_reason', label: 'Rejection reason' },
  // { id: 'gender', label: 'Gender' },
  { id: 'rejected_by_type', label: 'Rejected by' },
  { id: 'bgv_risk_level', label: 'Fraud status' },
];

const breakDownByBase = [
  { id: null, label: 'None' },
  { id: 'job_name', label: 'Job name' },
  { id: 'primary_recruiter', label: 'Primary recruiter' },
  { id: 'primary_hm', label: 'Primary hiring manager' },
  { id: 'appl_source', label: 'Source (on application)' },
  { id: 'department', label: 'Department' },
  // { id: 'appl_location', label: 'Location (on application)' },
  { id: 'rejection_type', label: 'Rejection type' },
  { id: 'rejection_reason', label: 'Rejection reason' },
  // { id: 'gender', label: 'Gender' },
  { id: 'rejected_by_type', label: 'Rejected by' },
  { id: 'bgv_risk_level', label: 'Fraud status' },
];

const metrics = [
  { id: 'passthrough_rate', label: 'Passthrough rate' },
  { id: 'overall_rate', label: 'Overall rate' },
  { id: 'time_in_stage', label: 'Time in stage' },
  // { id: 'time_to_reach_stage', label: 'Time to Reach Stage' },
];

const EMPTY_DATA: any[] = [];
const EMPTY_STAGE_MAPPING: Record<string, unknown> = {};

export const PipelineTable = ({
  metric = undefined,
  setMetric = undefined,
  isReadOnly = false,
  isPrintView = false,
  groupBy1 = undefined,
  groupBy2 = undefined,
  passThroughMetric = undefined,
  customData = undefined,
  customBreakdownChange = undefined,
  renderTitle = undefined,
  onDrillDownOpen = (_data) => {},
  customStageMapping = {},
  onSortChange = (_sortState) => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  feReportsV3 = false,
  isFullScreen = false,
  isFilterApplied = false,
}) => {
  const dispatch = useDispatch();
  const { feDrilldown, feFraudDetection } = useKFlags();
  const { consumer } = useContext(ReportSelectionContext);

  const groupBy = useMemo(
    () =>
      feFraudDetection
        ? groupByBase
        : groupByBase.filter((o) => o.id !== 'bgv_risk_level'),
    [feFraudDetection]
  );
  const breakDownBy = useMemo(
    () =>
      feFraudDetection
        ? breakDownByBase
        : breakDownByBase.filter((o) => o.id !== 'bgv_risk_level'),
    [feFraudDetection]
  );

  // Track current column visibility state for pipeline table
  const [currentTableColData, setCurrentTableColData] =
    useState(initialTableColData);

  // Update local state when initialTableColData changes
  useEffect(() => {
    if (feReportsV3) {
      setCurrentTableColData(initialTableColData);
    }
  }, [initialTableColData, feReportsV3]);

  // Custom handler for table column data changes in pipeline table
  const handlePipelineTableColDataChange = (tableColData) => {
    setCurrentTableColData(tableColData);

    // Call the original handler if provided
    if (onTableColDataChange) {
      onTableColDataChange(tableColData);
    }
  };

  const reduxState = useSelector((state: any) => state.pipelineReports);
  const {
    group: reduxGroup,
    table: reduxData,
    isTableLoading: reduxIsLoading,
    stageMapping: reduxStageMapping,
  } = reduxState || {};

  const { id: groupBy1Id, label: groupBy1Label = 'Time' } = groupBy1 || {};
  const { id: groupBy2Id } = groupBy2 || {};

  const group = feReportsV3 ? null : reduxGroup;
  const data = feReportsV3 ? EMPTY_DATA : reduxData;
  const isLoading = feReportsV3 ? false : reduxIsLoading;
  const stageMapping = feReportsV3 ? EMPTY_STAGE_MAPPING : reduxStageMapping;

  const getGroupValue = (value: any, fallbackValue: any) =>
    feReportsV3 ? value : value || fallbackValue;

  const drilldownGroup = {
    group_by_1: getGroupValue(groupBy1, group?.group_by_1),
    group_by_2: getGroupValue(groupBy2, group?.group_by_2),
  };

  const dataToUse = useMemo(
    () => (customData || data || []).filter((row) => row?.g1 !== null),
    [customData, data]
  );

  const stageMappingToUse = useMemo(
    () =>
      Object.keys(customStageMapping)?.length
        ? customStageMapping
        : stageMapping,
    [customStageMapping, stageMapping]
  );

  const field = Object.values(stageMappingToUse)?.length
    ? 's1_total_count'
    : 'application_review_total_count';

  const isDataEmpty = isReportTableEmpty(dataToUse, field);

  const { handleDrillDown } = useDrillDownHandler(feDrilldown, onDrillDownOpen);

  const columns = useMemo(() => {
    const formatRejectedByTypeValue = (value: string) => {
      const rejectedByTypeMap: Record<string, string> = {
        User: 'User',
        AtsAutoRejectionRule: 'Auto-rejection rule',
      };
      return rejectedByTypeMap[value] || value;
    };

    const BGV_RISK_LEVEL_MAP: Record<string, string> = {
      '0': 'Not evaluated',
      '-1': 'Failed',
      '1': 'No issues',
      '2': 'Inconclusive',
      '3': 'Suspicious',
    };

    const formatBgvRiskLevel = (value: string) =>
      BGV_RISK_LEVEL_MAP[value] || value;

    // Get current visibility state - use the most up-to-date state
    const getCurrentVisibility = () => {
      if (!feReportsV3) return {};

      // Use the current state which gets updated when visibility changes
      const visibility = currentTableColData?.visibility || {};
      return visibility;
    };

    // Helper function to get all visible stage columns in order
    const getVisibleStageColumns = (stageMappingList: any[]) => {
      if (!feReportsV3) {
        return stageMappingList.map((_, idx) => idx);
      }

      const visibility = getCurrentVisibility();
      const visibleStages = [];

      stageMappingList.forEach((stage, idx) => {
        const stageAccessor = `s${idx + 1}_total_count`;
        const isVisible = visibility[stageAccessor] !== false;

        if (isVisible) {
          visibleStages.push(idx);
        }
      });

      return visibleStages;
    };

    // Helper function to find the next visible stage for percentage calculation
    const findNextVisibleStage = (
      currentStageIndex: number,
      visibleStages: number[]
    ) => {
      const currentPos = visibleStages.indexOf(currentStageIndex);
      if (currentPos === -1 || currentPos === visibleStages.length - 1) {
        return -1;
      }

      const nextStageIndex = visibleStages[currentPos + 1];

      return nextStageIndex;
    };

    const stageMappingList = Object.values(stageMappingToUse);
    if (stageMappingList?.length) {
      const visibleStages = getVisibleStageColumns(stageMappingList);

      const headerColumn = renderHeader({
        accessor: 'header',
        title: groupBy1Label,
        width: isReadOnly ? 200 : 250,
        sort: true,
        groupBy: groupBy1Id,
        renderFooter: () => {
          return <KText size="sm-semi-bold">Total</KText>;
        },
      });

      const customHeaderColumn = {
        ...headerColumn,
        id: 'header',
        title: groupBy1Label,
        columnConfig: false, // This column should not be configurable
        Cell: (props: any) => {
          // Only format if the column is rejected_by_type
          const formattedValue =
            groupBy1Id === 'rejected_by_type'
              ? formatRejectedByTypeValue(props.cell.value)
              : groupBy1Id === 'bgv_risk_level'
                ? formatBgvRiskLevel(props.cell.value)
                : props.cell.value;

          const modifiedProps = {
            ...props,
            cell: {
              ...props.cell,
              value: formattedValue,
            },
          };

          return headerColumn.Cell(modifiedProps);
        },
      };

      const columns = [customHeaderColumn];

      // Generate stage columns and percentage columns between visible stages only
      stageMappingList?.forEach((stage, idx) => {
        const currstage = idx;
        const accessor = `s${currstage + 1}_total_count`;

        // Add stage column
        const stageColumn = {
          ...renderCell({
            title: stage as string,
            accessor: accessor,
            width: 180,
            sort: true,
            columnConfig: feReportsV3 ? 'skipreorder' : true,
            render: (_, row) => {
              return (
                <Flex
                  cursor={feDrilldown ? 'pointer' : 'default'}
                  _hover={{
                    textDecoration: feDrilldown ? 'underline' : 'none',
                  }}
                  onClick={() =>
                    handleDrillDown(
                      row,
                      accessor,
                      false,
                      drilldownGroup,
                      stage as string,
                      row?.[accessor]
                    )
                  }
                >
                  {row?.[accessor]}
                </Flex>
              );
            },
            renderFooter: (info) => {
              const row = info?.rows?.[0]?.original;
              return (
                <Flex
                  cursor={feDrilldown ? 'pointer' : 'default'}
                  _hover={{
                    textDecoration: feDrilldown ? 'underline' : 'none',
                  }}
                  onClick={() =>
                    handleDrillDown(
                      row,
                      accessor,
                      true,
                      drilldownGroup,
                      stage as string,
                      row?.dynamic_total?.[idx]
                    )
                  }
                >
                  <KText size="sm-semi-bold">{row?.dynamic_total?.[idx]}</KText>
                </Flex>
              );
            },
          }),
          id: accessor,
          ...(feReportsV3 && { relatedColumns: [`pipeline_metric_${idx}`] }),
        };

        (columns as any[]).push(stageColumn);

        // Only add percentage column if this stage is visible and there's a next visible stage
        const nextVisibleStageIndex = findNextVisibleStage(
          currstage,
          visibleStages
        );
        const isCurrentStageVisible = visibleStages.includes(currstage);

        if (isCurrentStageVisible && nextVisibleStageIndex !== -1) {
          const metricAccessor = `pipeline_metric_${idx}`;
          const percentageColumn = {
            ...renderCell({
              title: '',
              accessor: metricAccessor,
              sort: false, // Explicitly disable sorting for this column
              columnConfig: feReportsV3 ? 'hidden' : true,
              render: (_, row) => {
                if (metric === 'time_in_stage') {
                  // Don't modify time calculation - keep original logic
                  return secondsToHumanTime(
                    parseInt(row?.[`s${currstage + 1}_latency`]) /
                      parseInt(row?.[`s${currstage + 1}_total_count`])
                  );
                }

                let current, next;

                if (metric === 'passthrough_rate') {
                  current =
                    parseInt(row?.[`s${currstage + 1}_total_count`]) || 1;
                  next = parseInt(
                    row?.[`s${nextVisibleStageIndex + 1}_total_count`]
                  );
                }

                if (metric === 'overall_rate') {
                  current = parseInt(row.s1_total_count) || 1;
                  next = parseInt(
                    row?.[`s${nextVisibleStageIndex + 1}_total_count`]
                  );
                }

                const percentage = Math.round((next / current) * 100);
                const color =
                  percentage > 50
                    ? '#00A91B'
                    : percentage > 25
                      ? '#FF9900'
                      : '#FF0000';
                return (
                  <Flex alignItems="center" gap="8px">
                    <KText color={color}>{percentage}%</KText>
                    {metric === 'passthrough_rate' ? (
                      <Icon as={ArrowRightIcon} color={color} />
                    ) : null}
                  </Flex>
                );
              },
              renderFooter: (info) => {
                const row = info?.rows?.[0]?.original;
                if (metric === 'time_in_stage') {
                  return '-';
                }

                let current, previous;
                if (metric === 'passthrough_rate') {
                  current = parseInt(row?.dynamic_total[nextVisibleStageIndex]);
                  previous = parseInt(row?.dynamic_total[currstage]) || 1;
                }
                if (metric === 'overall_rate') {
                  current = parseInt(row?.dynamic_total[nextVisibleStageIndex]);
                  previous = parseInt(row?.dynamic_total[0]) || 1;
                }
                const percentage = Math.round((current / previous) * 100);
                const color =
                  percentage > 50
                    ? '#00A91B'
                    : percentage > 25
                      ? '#FF9900'
                      : '#FF0000';
                return (
                  <Flex alignItems="center" gap="8px">
                    <KText color={color} className="percentage">
                      {percentage}%
                    </KText>
                    {metric === 'passthrough_rate' && (
                      <Icon
                        as={ArrowRightIcon}
                        color={color}
                        className="arrow-icon"
                      />
                    )}
                  </Flex>
                );
              },
              width: 180,
            }),
            id: metricAccessor,
          };

          (columns as any[]).push(percentageColumn);
        }
      });

      return columns;
    }

    const headerColumn = renderHeader({
      accessor: 'header',
      title: groupBy1Label ?? 'Time',
      width: isReadOnly ? 150 : 200,
      sort: true,
      groupBy: groupBy1Id,
      breakDownBy: groupBy2Id,
      isReadOnly,
      renderFooter: () => {
        return <KText size="sm-semi-bold">Total</KText>;
      },
    });

    const customHeaderColumn = {
      ...headerColumn,
      Cell: (props: any) => {
        // Only format if the column is rejected_by_type
        const formattedValue =
          groupBy1Id === 'rejected_by_type'
            ? formatRejectedByTypeValue(props.cell.value)
            : props.cell.value;

        const modifiedProps = {
          ...props,
          cell: {
            ...props.cell,
            value: formattedValue,
          },
        };

        return headerColumn.Cell(modifiedProps);
      },
    };

    // Add column configuration properties
    (customHeaderColumn as any).id = 'header';
    (customHeaderColumn as any).title = groupBy1Label;
    (customHeaderColumn as any).columnConfig = feReportsV3 ? 'locked' : false; // Time column locked when feReportsV3 is enabled

    // Define static stage structure
    const staticStages = [
      {
        title: 'Application review stages',
        accessor: 'application_review_total_count',
        timeAccessor: 'time_in_application_review',
      },
      {
        title: 'Interview stages',
        accessor: 'interviews_total_count',
        timeAccessor: 'time_in_interviews',
      },
      {
        title: 'Offer stages',
        accessor: 'offer_total_count',
        timeAccessor: 'time_in_offer',
      },
      {
        title: 'Hired stages',
        accessor: 'hired_total_count',
        timeAccessor: null,
      },
    ];

    // Get visible stages for static structure
    const getVisibleStaticStages = () => {
      if (!feReportsV3) {
        return staticStages.map((_, idx) => idx);
      }

      const visibility = getCurrentVisibility();
      const visibleStages = [];

      staticStages.forEach((stage, idx) => {
        const isVisible = visibility[stage.accessor] !== false;

        if (isVisible) {
          visibleStages.push(idx);
        }
      });

      return visibleStages;
    };

    const visibleStaticStages = getVisibleStaticStages();
    const columns: any[] = [customHeaderColumn];

    // Generate columns for each stage
    staticStages.forEach((stage, idx) => {
      // Add stage column
      const stageColumn = {
        ...renderCell({
          title: stage.title,
          accessor: stage.accessor,
          width: idx === 0 ? 180 : isReadOnly ? 120 : 180,
          sort: true,
          columnConfig: feReportsV3 ? 'skipreorder' : true,
          render: (_, row) => {
            return (
              <Flex
                cursor={feDrilldown ? 'pointer' : 'default'}
                _hover={{
                  textDecoration: feDrilldown ? 'underline' : 'none',
                }}
                onClick={() =>
                  handleDrillDown(
                    row,
                    stage.accessor,
                    false,
                    drilldownGroup,
                    stage.title,
                    row?.[stage.accessor]
                  )
                }
              >
                {row?.[stage.accessor]}
              </Flex>
            );
          },
          renderFooter: (info) => {
            const row = info?.rows?.[0]?.original;
            return (
              <Flex
                cursor={feDrilldown ? 'pointer' : 'default'}
                _hover={{
                  textDecoration: feDrilldown ? 'underline' : 'none',
                }}
                onClick={() =>
                  handleDrillDown(
                    row,
                    stage.accessor,
                    true,
                    drilldownGroup,
                    stage.title,
                    row?.[`${stage.accessor}_total`]
                  )
                }
              >
                <KText size="sm-semi-bold">
                  {row?.[`${stage.accessor}_total`]}
                </KText>
              </Flex>
            );
          },
        }),
        id: stage.accessor,
        ...(feReportsV3 && { relatedColumns: [`pipeline_metric_${idx + 1}`] }),
      };

      (columns as any[]).push(stageColumn);

      // Add percentage column if this stage is visible and there's a next visible stage
      const currentPos = visibleStaticStages.indexOf(idx);
      const hasNextVisibleStage =
        currentPos !== -1 && currentPos < visibleStaticStages.length - 1;
      const isCurrentStageVisible = visibleStaticStages.includes(idx);

      if (isCurrentStageVisible && hasNextVisibleStage) {
        const nextVisibleStageIndex = visibleStaticStages[currentPos + 1];
        const nextStage = staticStages[nextVisibleStageIndex];
        const metricAccessor = `pipeline_metric_${idx + 1}`;

        const percentageColumn = {
          ...renderCell({
            title: '',
            accessor: metricAccessor,
            sort: false,
            columnConfig: feReportsV3 ? 'hidden' : true,
            render: (_, row) => {
              if (metric === 'time_in_stage' && stage.timeAccessor) {
                // Don't modify time calculation - keep original logic
                const stageCount = parseInt(row[stage.accessor]);
                return secondsToHumanTime(
                  parseInt(row[stage.timeAccessor]) / stageCount
                );
              }

              let current, previous;
              if (metric === 'passthrough_rate') {
                current = parseInt(row[nextStage.accessor]) || 0;
                previous = parseInt(row[stage.accessor]) || 1;
              }
              if (metric === 'overall_rate') {
                current = parseInt(row[nextStage.accessor]) || 0;
                previous = parseInt(row.application_review_total_count) || 1;
              }

              const percentage = Math.round((current / previous) * 100);
              const color =
                percentage > 50
                  ? '#00A91B'
                  : percentage > 25
                    ? '#FF9900'
                    : '#FF0000';
              return (
                <Flex alignItems="center" gap="8px">
                  <KText color={color} className="percentage">
                    {percentage}%
                  </KText>
                  {metric === 'passthrough_rate' && (
                    <Icon
                      as={ArrowRightIcon}
                      color={color}
                      className="arrow-icon"
                    />
                  )}
                </Flex>
              );
            },
            renderFooter: (info) => {
              const row = info?.rows?.[0]?.original;
              if (metric === 'time_in_stage') {
                return '-';
              }

              let current, previous;
              if (metric === 'passthrough_rate') {
                current = parseInt(row[`${nextStage.accessor}_total`]) || 0;
                previous = parseInt(row[`${stage.accessor}_total`]) || 1;
              }
              if (metric === 'overall_rate') {
                current = parseInt(row[`${nextStage.accessor}_total`]) || 0;
                previous =
                  parseInt(row.application_review_total_count_total) || 1;
              }

              const percentage = Math.round((current / previous) * 100);
              const color =
                percentage > 50
                  ? '#00A91B'
                  : percentage > 25
                    ? '#FF9900'
                    : '#FF0000';
              return (
                <Flex alignItems="center" gap="8px">
                  <KText color={color} className="percentage">
                    {percentage}%
                  </KText>
                  {metric === 'passthrough_rate' && (
                    <Icon
                      as={ArrowRightIcon}
                      color={color}
                      className="arrow-icon"
                    />
                  )}
                </Flex>
              );
            },
            width: isReadOnly ? 95 : 180,
          }),
          id: metricAccessor,
        };

        (columns as any[]).push(percentageColumn);
      }
    });

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    groupBy1Label,
    metric,
    stageMappingToUse,
    groupBy1Id,
    groupBy2Id,
    feReportsV3,
    currentTableColData,
  ]);

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  const mergedTableColStyles = useMemo(
    () => ({
      ...tableColStyles,
      hideReorderColumns: feReportsV3,
    }),
    [tableColStyles, feReportsV3]
  );

  const tableLayoutStyles = useMemo(() => ({ borderBottom: 'none' }), []);

  const defaultSortBy = useMemo(() => [{ id: 'header', desc: false }], []);

  // Single onChange handler - conditional Redux vs customBreakdownChange
  const handleBreakdownChange = (key: string, option: Option) => {
    switch (key) {
      case 'group_by_1':
        dispatch(applyGroup({ key: 'group_by_1', value: option }));
        break;
      case 'group_by_2':
        dispatch(applyGroup({ key: 'group_by_2', value: option }));
        break;
      case 'metrics':
        setMetric?.(option.id as string);
        break;
      default:
        break;
    }
  };

  // Configuration array for BreakdownBy components - use props only, no Redux fallbacks
  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: groupBy,
      selected: feReportsV3
        ? groupBy1 || groupBy[0]
        : groupBy1 || group?.group_by_1 || groupBy[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: breakDownBy,
      selected: feReportsV3
        ? groupBy2 || breakDownBy[0]
        : groupBy2 || group?.group_by_2 || breakDownBy[0],
    },
    {
      key: 'metrics',
      prefix: 'Metrics',
      options: metrics,
      selected:
        passThroughMetric ||
        metrics?.find((m) => m.id === metric) ||
        metrics[0],
    },
  ];

  if (isFilterApplied) {
    return (
      <EmptyReportChart
        text="Filters not applicable"
        subText="The applied filters are not applicable for this block"
      />
    );
  }

  return (
    <Box h="calc(100% - 25px)" w="100%">
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems={'flex-start'}
          justifyContent={'end'}
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle && renderTitle?.()}
          <Flex
            gap="8px"
            justifyContent="end"
            mb="8px"
            mr={
              isPrintView
                ? '0'
                : isDataEmpty
                  ? '35px'
                  : isEditMode
                    ? '55px'
                    : '35px'
            }
            data-test-id="section-pipeline-controls"
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  customBreakdownChange
                    ? customBreakdownChange(key, option)
                    : handleBreakdownChange(key, option)
                }
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isLoading ? (
        <CenterSpinner h="400px" />
      ) : (
        <EmptyDataWrapper data={dataToUse} field={field}>
          {/* <Table
            tableId="pipeline-table"
            skipLocalStorage={skipLocalStorage}
            isColumnLevelSorting
            defaultSortBy={[{ id: 'header', desc: false }]}
            enableColumnConfig={isEditMode}
            columns={columns}
            data={dataToUse}
            enableFooter={true}
            layoutScrollContainerStyles="c-data-table wide-content table-container"
            enableExpanded={true}
            layoutStyles={{
              borderBottom: 'none',
            }}
            getTableState={handleSortChange}
            tableColStyles={{
              ...tableColStyles,
              hideReorderColumns: feReportsV3,
            }}
            onTableColDataChange={
              feReportsV3
                ? handlePipelineTableColDataChange
                : onTableColDataChange
            }
            initialTableColData={initialTableColData}
          /> */}
          <Box h="100%" w="100%" overflow="hidden !important">
            <Table
              tableId="pipeline-table"
              skipLocalStorage={skipLocalStorage}
              isColumnLevelSorting
              defaultSortBy={defaultSortBy}
              enableColumnConfig={isEditMode}
              columns={columns}
              data={dataToUse}
              enableFooter={true}
              layoutScrollContainerStyles="c-data-table wide-content table-container"
              enableExpanded={true}
              layoutStyles={tableLayoutStyles}
              getTableState={handleSortChange}
              tableColStyles={mergedTableColStyles}
              onTableColDataChange={
                feReportsV3
                  ? handlePipelineTableColDataChange
                  : onTableColDataChange
              }
              initialTableColData={initialTableColData}
            />
          </Box>
        </EmptyDataWrapper>
      )}
    </Box>
  );
};
